import React, {
  useLayoutEffect,
  useMemo,
  useState,
  useEffect,
  useRef,
} from 'react';
import {
  Box,
  Container,
  Divider as MuiDivider,
  Typography,
  Stack,
  Chip,
  Button,
  Link,
} from '@mui/material';
import { gql, useQuery } from '@apollo/client';
import { useParams, useLocation } from 'react-router';
import { Navigate, Link as RouterLink } from 'react-router-dom';
import { MapPin } from 'react-feather';

import { LoadingIndicator } from '@pv/common/components';
import { PVBlack } from '@pv/common/colors';
import { useGuestSpaceFavorites } from '../../../hooks/useGuestSpaceFavorites';
import { useAuth } from '../../../contexts/AuthContext';
import { FavoriteButton } from '../../../components/FavoriteButton';

import { Image } from '../../../components/ImageCarousel';
import { ProfileGoogleMap } from '../../../components/ProfileGoogleMap';
import { useAvailableSpaces } from '../../../helpers/api/graphql';
import { VenueLocationDetailsLink } from '../../../components/VenueLocationDetailsLink';
import {
  SpacePreview,
  PreviewableSpace,
} from '../../../components/SpacePreview';
import { AvailabilityForm } from './AvailabilityForm';
import { ContactUsButton } from '../../../components/ContactUsButton';
import { ProfileFooter } from '../../../components/ProfileFooter';
import { CapacityInfo } from '../../../components/CapacityInfo';
import { ReviewsInfo } from '../../../components/ReviewsInfo';
import { VenueSeoMarkup } from '../VenueSeoMarkup';
import { VenueMenuItems } from '../../../components/VenueMenuItems';
import { SpaceProfileContext, useSpaceProfile } from './useSpaceProfile';
import {
  SpacePageSpaceQuery,
  SpacePageSpaceQueryVariables,
  SpacePageVenueQuery,
  SpacePageVenueQueryVariables,
} from '@pv/common/graphql/operations';
import { ImageGallery } from './ImageGallery';
import { AmenityBadge } from '../../../components/AmenityBadge';

const VENUE_QUERY = gql`
  query SpacePageVenueQuery($slug: String!) {
    marketplaceVenue(slug: $slug) {
      id
      slug
      name
      description
      address {
        id
        streetAddress1
        streetAddress2
        city
        state
        latitude
        longitude
      }
      minGroupSize
      helloLeadFormUrl
      images {
        id
        url
      }
      expressBookSettings {
        id
        minLeadTimeDays
        maxLeadTimeDays
        minEventDurationHours
        maxEventDurationHours
      }
      averageResponseTime
      availabilityEnabled
      hoursOfOperation {
        id
        dayOfWeek
        isOpen
        startTime
        endTime
      }
      stripeAccount {
        id
        stripeAccountId
      }
      menu {
        id
        menuItems(displayOnExpressBook: true) {
          id
        }
        previewMenuItems(limit: 4) {
          id
          name
          image(width: 500, height: 300)
        }
      }
      defaultPaymentProvider
      priceCategory
      liveAvailability
      googlePlaceInfo {
        id
        rating
        reviewCount
        mapsUrl
      }
    }
  }
`;

const SPACE_QUERY = gql`
  query SpacePageSpaceQuery($slug: String!) {
    marketplaceSpace(slug: $slug) {
      id
      slug
      name
      description
      seated
      standing
      images {
        id
        url
      }
      expressBookEnabled
      amenities {
        id
        name
        slug
        icon
        description
      }
    }
  }
`;

const LiveAvailabilityChip = () => (
  <Box>
    <Chip
      label="Live Availability"
      title="These dates and times reflect the venue’s most current availability, updated directly from their calendar."
      sx={{
        backgroundColor: '#FFD3AC',
        color: '#000000',
        fontWeight: 'bold',
      }}
    />
  </Box>
);

const Wrapper = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  useLayoutEffect(() => {
    document.documentElement.scrollTo(0, 0);
  }, [location.pathname]);
  return <Container>{children}</Container>;
};

const Layout = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      display: 'grid',
      gap: '0 48px',
      gridTemplateAreas: {
        xs: '"header" "aside" "body"',
        md: '"header" "body" "aside"',
      },
      gridTemplateColumns: {
        xs: 'auto',
        md: 'repeat(2, 1fr)',
        lg: 'auto auto 384px',
      },
      gridTemplateRows: {
        xs: 'auto auto 1fr',
        md: 'auto 1fr',
      },
      padding: {
        md: '0 32px',
        sm: '0 8px',
        xs: '0 8px',
      },
    }}
  >
    {children}
  </Box>
);

const Header = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      gridArea: 'header',
      gridColumnStart: 1,
      gridColumnEnd: { xs: 1, lg: 3 },
    }}
  >
    {children}
  </Box>
);

const Body = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      gridArea: 'body',
      gridColumn: 1,
      gridColumnEnd: { xs: 1, lg: 3 },
    }}
  >
    {children}
  </Box>
);

const Aside = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      gridArea: 'aside',
      gridColumn: { xs: 1, md: 2, lg: 3 },
      gridRowStart: { xs: 2, md: 1 },
      gridRowEnd: { xs: 0, md: 0 },
    }}
  >
    {children}
  </Box>
);

const Divider = () => <MuiDivider sx={{ marginBottom: '24px' }} />;

const SectionHeader = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="h2"
    sx={{
      fontWeight: '700',
      fontSize: '24px',
      lineHeight: '133.4%',
      marginBottom: '24px',
    }}
  >
    {children}
  </Typography>
);

const VenueInfo = () => {
  const { venue } = useSpaceProfile();

  return (
    <Stack spacing={2} sx={{ marginBottom: '24px' }}>
      <VenueLocationDetailsLink
        venueName={venue.name}
        venueSlug={venue.slug}
        venueAddress={venue.address?.city}
        fontSize="20px"
        venueNameColor={PVBlack}
        venueAddressColor={PVBlack}
        boldName
      />
      {venue.address && (
        <Stack spacing={1} direction="row" alignItems={'center'}>
          <MapPin size={'20px'} />
          <address>
            <Typography sx={{ fontStyle: 'normal', fontWeight: '500' }}>
              {[
                venue.address.streetAddress1,
                venue.address.streetAddress2,
                venue.address.city,
                venue.address.state,
              ]
                .filter((v) => !!v)
                .join(', ')}
            </Typography>
          </address>
        </Stack>
      )}
    </Stack>
  );
};

const TextSection = ({ body, title }: { body: string; title: string }) => (
  <Box sx={{ marginBottom: '24px' }}>
    <SectionHeader>{title}</SectionHeader>
    <Typography sx={{ whiteSpace: 'pre-wrap' }}>{body}</Typography>
  </Box>
);

const VenueLocation = ({
  latitude,
  longitude,
  slug,
  name,
}: {
  latitude: number;
  longitude: number;
  slug: string;
  name: string;
}) => {
  const venuePosition = { lat: latitude, lng: longitude };
  const mappableVenue = {
    key: slug,
    name,
    position: venuePosition,
  };

  return (
    <Box sx={{ marginBottom: '24px' }}>
      <SectionHeader>Location</SectionHeader>
      <ProfileGoogleMap
        center={venuePosition}
        mapStyle={{
          width: '100%',
          height: '320px',
          borderRadius: '12px',
        }}
        mappableItems={[mappableVenue]}
      />
    </Box>
  );
};

const OtherSpaces = ({ spaces }: { spaces: PreviewableSpace[] }) => {
  const { venue } = useSpaceProfile();
  return (
    <Box sx={{ marginBottom: '24px' }}>
      <SectionHeader>Other Spaces at {venue.name}</SectionHeader>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)',
          },
          gridAutoRows: '1fr',
          gap: '16px',
        }}
      >
        {spaces.map((space) => (
          <SpacePreview space={space} key={space.pathname} />
        ))}
      </Box>
    </Box>
  );
};

const Availability = ({ children }: { children: React.ReactNode }) => {
  const { venue } = useSpaceProfile();

  return (
    <Box
      sx={{
        padding: '24px',
        borderRadius: '16px',
        border: '1px solid rgba(0,0,0,0.1)',
        boxShadow: '0px 2px 12px 0px rgba(0, 0, 0, 0.08)',
      }}
    >
      <Stack marginBottom={'24px'} gap={'16px'}>
        {venue.liveAvailability && <LiveAvailabilityChip />}
        <Typography
          variant="h2"
          sx={{
            fontWeight: '700',
            fontSize: '24px',
            lineHeight: '133.4%',
          }}
        >
          Select Date
        </Typography>
      </Stack>
      {children}
    </Box>
  );
};

const SubNavigation = ({ show }: { show: boolean }) => {
  const { venue } = useSpaceProfile();
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const elementPosition = element.offsetTop;
      const offset = 80; // offset the scroll to account for the subnav height
      window.scrollTo({
        top: elementPosition - offset,
        behavior: 'smooth',
      });
    }
  };

  const navItems = [
    { id: 'details-section', label: 'Details' },
    { id: 'amenities-section', label: 'Amenities' },
    { id: 'about-venue-section', label: 'About the venue' },
    { id: 'location-section', label: 'Location' },
    { id: 'menu-section', label: `Event menu` },
    { id: 'other-spaces-section', label: 'Other spaces' },
  ];

  const buttonStyles = {
    color: 'text.primary',
    fontWeight: 500,
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.04)',
    },
  };

  return (
    <Box
      sx={{
        display: { xs: 'none', md: 'block' },
        position: 'sticky',
        top: 0,
        zIndex: 100,
        backgroundColor: 'white',
        marginBottom: show ? '32px' : '0',
        padding: show ? '16px 0' : '0',
        borderBottom: show ? '1px solid rgba(0,0,0,0.1)' : 'none',
        opacity: show ? 1 : 0,
        transform: show ? 'translateY(0)' : 'translateY(-20px)',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: show
          ? '0 2px 8px rgba(0,0,0,0.1)'
          : '0 2px 8px rgba(0,0,0,0)',
        pointerEvents: show ? 'auto' : 'none',
        visibility: show ? 'visible' : 'hidden',
        width: '100vw',
        marginLeft: 'calc(-50vw + 50%)',
        marginRight: 'calc(-50vw + 50%)',
        height: show ? 'auto' : '0',
        overflow: 'hidden',
        minHeight: show ? 'auto' : '0',
      }}
    >
      <Container>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 2, sm: 3 }}
          justifyContent="flex-start"
          alignItems="center"
          flexWrap="wrap"
        >
          {navItems.map((item) => (
            <Button
              key={item.id}
              variant="text"
              onClick={() => scrollToSection(item.id)}
              sx={buttonStyles}
            >
              {item.label}
            </Button>
          ))}
        </Stack>
      </Container>
    </Box>
  );
};

export const SpacePage = () => {
  const { spaceSlug, venueSlug } = useParams();
  const [showSubNavigation, setShowSubNavigation] = useState(false);
  const imageCarouselRef = useRef<HTMLDivElement>(null);

  const {
    data: fetchVenueResponse,
    loading: venueQueryLoading,
    error: venueQueryError,
  } = useQuery<SpacePageVenueQuery, SpacePageVenueQueryVariables>(VENUE_QUERY, {
    variables: { slug: venueSlug || '' },
    skip: !venueSlug,
  });

  const venue = fetchVenueResponse?.marketplaceVenue;
  const googlePlaceInfo = venue?.googlePlaceInfo;

  const {
    data: fetchSpaceResponse,
    loading: spaceQueryLoading,
    error: spaceQueryError,
  } = useQuery<SpacePageSpaceQuery, SpacePageSpaceQueryVariables>(SPACE_QUERY, {
    variables: {
      slug: spaceSlug || '',
    },
    skip: !spaceSlug,
  });

  const space = fetchSpaceResponse?.marketplaceSpace;

  const { data: fetchAvailableSpacesResponse } = useAvailableSpaces(
    { venueSlug },
    !venueSlug,
  );

  const { favoriteSpaces, toggleFavorite } = useGuestSpaceFavorites();
  const { guest, setSignupDialogOpen } = useAuth();

  const isFavorited = favoriteSpaces.some(
    (favoriteSpace) => favoriteSpace.slug === space?.slug,
  );

  const images = useMemo(() => {
    let images: { url: string }[] = [];

    if (space) {
      images = [
        ...images,
        ...space.images.filter(
          (image): image is { url: string } => !!image.url,
        ),
      ];
    }

    if (venue) {
      images = [
        ...images,
        ...venue.images.filter(
          (image): image is { url: string } => !!image.url,
        ),
      ];
    }

    if (!images.length) {
      images = [{ url: '/default_space_image.png' }];
    }

    return images;
  }, [space, venue]);

  const availableSpaces = fetchAvailableSpacesResponse?.availableSpaces || [];

  useEffect(() => {
    const handleScroll = () => {
      if (imageCarouselRef.current) {
        const rect = imageCarouselRef.current.getBoundingClientRect();
        const isScrolledPast = rect.bottom < 0;
        setShowSubNavigation(isScrolledPast);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleToggleFavorite = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!guest) {
      setSignupDialogOpen(true);
      return;
    }

    if (space?.slug) {
      await toggleFavorite(space.slug, isFavorited);
    }
  };

  if (spaceQueryLoading || venueQueryLoading) {
    return <LoadingIndicator />;
  }

  const isSpaceNotFoundError =
    spaceQueryError?.graphQLErrors[0].message === 'No space found';
  const isVenueNotFoundError =
    venueQueryError?.graphQLErrors[0].message === 'No venue found';

  const shouldShowCapacityRow = space && venue;

  if (isSpaceNotFoundError || isVenueNotFoundError || !space || !venue) {
    return <Navigate to="/not-found" replace={false} />;
  }

  const spaceProfileContextValue = {
    space: {
      name: space.name,
      slug: space.slug,
      expressBookEnabled: space.expressBookEnabled,
      images: space.images.filter(
        (image): image is { url: string } => !!image.url,
      ),
    },
    venue: {
      name: venue.name,
      slug: venue.slug,
      address: venue.address
        ? {
            streetAddress1: venue.address.streetAddress1 || undefined,
            streetAddress2: venue.address.streetAddress2 || undefined,
            city: venue.address.city || undefined,
            state: venue.address.state || undefined,
          }
        : undefined,
      liveAvailability: venue.liveAvailability,
      menu: {
        previewMenuItems: venue.menu.previewMenuItems.filter(
          (
            item,
          ): item is {
            id: string;
            name: string;
            image: string;
          } => !!item.image,
        ),
      },
      images: venue.images.filter(
        (image): image is { url: string } => !!image.url,
      ),
    },
  };

  return (
    <Wrapper>
      <SpaceProfileContext.Provider value={spaceProfileContextValue}>
        <VenueSeoMarkup venueSlug={venue.slug} />
        <main>
          <Box
            ref={imageCarouselRef}
            sx={{
              marginBottom: {
                md: '36px',
                sm: '24px',
                xs: '24px',
                position: 'relative',
              },
            }}
          >
            <ImageGallery />
            {space && venue && (
              <>
                <Image
                  sx={{
                    height: { xs: '300px', sm: '360px', md: '540px' },
                    pointerEvents: 'none',
                  }}
                  src={images[0]?.url}
                  alt=""
                  key={images[0]?.url}
                />
                <div
                  style={{
                    position: 'absolute',
                    top: '16px',
                    right: '16px',
                    zIndex: 10,
                  }}
                >
                  <FavoriteButton
                    isFavorited={isFavorited}
                    onClick={handleToggleFavorite}
                  />
                </div>
              </>
            )}
          </Box>
          {venue && <SubNavigation show={showSubNavigation} />}
          <Box sx={{ marginBottom: '48px' }}>
            <Layout>
              <Header>
                {space && (
                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: {
                        lg: '50px',
                        md: '50px',
                        sm: '36px',
                        xs: '36px',
                      },
                      lineHeight: {
                        lg: '60px',
                        md: '60px',
                        sm: '44px',
                        xs: '44px',
                      },
                      paddingBottom: '16px',
                      fontFamily: 'Gazpacho',
                      fontWeight: 'bold',
                    }}
                  >
                    {space.name}
                  </Typography>
                )}
                {venue && <VenueInfo />}

                {shouldShowCapacityRow && (
                  <>
                    <Divider />
                    <Box sx={{ marginBottom: '24px' }}>
                      <CapacityInfo
                        space={{
                          seated: space.seated || undefined,
                          standing: space.standing || undefined,
                        }}
                        venue={{
                          minGroupSize: venue.minGroupSize || undefined,
                          priceCategory: venue.priceCategory || undefined,
                        }}
                      />
                    </Box>
                  </>
                )}
              </Header>

              <Body>
                <Divider />
                {googlePlaceInfo &&
                  googlePlaceInfo.rating &&
                  googlePlaceInfo.reviewCount &&
                  googlePlaceInfo.mapsUrl && (
                    <ReviewsInfo
                      googlePlaceInfo={{
                        rating: googlePlaceInfo.rating,
                        reviewCount: googlePlaceInfo.reviewCount,
                        mapsUrl: googlePlaceInfo.mapsUrl,
                      }}
                    />
                  )}
                {space?.description && (
                  <Box id="details-section">
                    <TextSection body={space.description} title="Details" />
                  </Box>
                )}
                {space?.amenities && space.amenities.length > 0 && (
                  <>
                    <Divider />
                    <Box id="amenities-section" sx={{ marginBottom: '24px' }}>
                      <SectionHeader>Amenities</SectionHeader>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {space.amenities.map((amenity) => (
                          <AmenityBadge key={amenity.id} amenity={amenity} />
                        ))}
                      </Stack>
                    </Box>
                  </>
                )}
                {venue?.description && (
                  <>
                    <Divider />
                    <Box id="about-venue-section">
                      <TextSection
                        body={venue.description}
                        title="About the venue"
                      />
                    </Box>
                  </>
                )}
                {venue?.address?.latitude && venue?.address?.longitude && (
                  <>
                    <Divider />
                    <Box id="location-section">
                      <VenueLocation
                        latitude={venue.address.latitude}
                        longitude={venue.address.longitude}
                        slug={venue.slug}
                        name={venue.name}
                      />
                    </Box>
                  </>
                )}
              </Body>
              <Aside>
                <Box sx={{ display: { md: 'none' } }}>
                  <Divider />
                </Box>
                {space && venue && (
                  <Stack spacing={3}>
                    {venue.availabilityEnabled && (
                      <Availability>
                        <AvailabilityForm venue={venue} />
                      </Availability>
                    )}
                    <ContactUsButton leadFormUrl={venue.helloLeadFormUrl} />
                    {venue.averageResponseTime && (
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Typography variant="body1" color="text.secondary">
                          {venue.name} typically responds within{' '}
                          {venue.averageResponseTime}.
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                )}
              </Aside>
            </Layout>

            <Stack sx={{ padding: { md: '0 32px' } }}>
              {venue.menu.menuItems?.length > 0 && (
                <>
                  <Divider />
                  <Box id="menu-section" sx={{ marginBottom: '24px' }}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      justifyContent="space-between"
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      sx={{ marginBottom: '24px' }}
                    >
                      <SectionHeader>{venue.name}'s Event Menu</SectionHeader>
                      <Link
                        component={RouterLink}
                        to={`/venues/${venue.slug}/menu`}
                        sx={{
                          textDecoration: 'none',
                          fontWeight: 500,
                          fontSize: '16px',
                          lineHeight: '150%',
                          color: 'text.primary',
                        }}
                      >
                        View Event Menu &gt;
                      </Link>
                    </Stack>
                    <VenueMenuItems
                      menuItems={venue.menu.previewMenuItems.filter(
                        (
                          item,
                        ): item is {
                          id: string;
                          name: string;
                          image: string;
                        } => !!item.image,
                      )}
                    />
                  </Box>
                </>
              )}
              {space && availableSpaces?.length > 1 && (
                <>
                  <Divider />
                  <Box id="other-spaces-section">
                    <OtherSpaces
                      spaces={availableSpaces
                        .filter((s) => s.slug !== space.slug)
                        .map((availableSpace) => ({
                          slug: availableSpace.slug,
                          name: availableSpace.name,
                          description: availableSpace.description,
                          seated: availableSpace.seated || undefined,
                          standing: availableSpace.standing || undefined,
                          images: availableSpace.images.filter(
                            (image): image is { url: string } => !!image.url,
                          ),
                          pathname: availableSpace.pathname,
                          venue: {
                            liveAvailability:
                              availableSpace.venue.liveAvailability,
                            images: availableSpace.venue.images.filter(
                              (image): image is { url: string } => !!image.url,
                            ),
                            profileBanner:
                              availableSpace.venue.profileBanner || undefined,
                            name: availableSpace.venue.name,
                            slug: availableSpace.venue.slug,
                            priceCategory: availableSpace.venue.priceCategory,
                            address: availableSpace.venue.address
                              ? {
                                  city: availableSpace.venue.address.city || '',
                                  state:
                                    availableSpace.venue.address.state || '',
                                }
                              : undefined,
                          },
                        }))}
                    />
                  </Box>
                </>
              )}
            </Stack>
          </Box>
        </main>
        <footer>
          <Divider />
          <ProfileFooter />
        </footer>
      </SpaceProfileContext.Provider>
    </Wrapper>
  );
};
